import { useCallback, useState } from "react";
import {
  listAssetsReq,
  listAssetAssigneesReq,
  createAssetReq,
  updateAssetReq,
  deleteAssetReq,
  assignAssetReq,
  returnAssetReq,
  disposeAssetReq,
} from "services/api";
import { ASSETS_MESSAGES } from "./assets.constants";

const parseErrorMessage = (err, fallbackMessage) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.message || data?.errors?.[0]?.msg || fallbackMessage;
};

function useAssets(adminId) {
  const [assets, setAssets] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!adminId) return;
    setIsLoading(true);
    try {
      const [assetsRes, assigneesRes] = await Promise.all([
        listAssetsReq({ admin_id: adminId }),
        listAssetAssigneesReq({ admin_id: adminId }),
      ]);
      setAssets(Array.isArray(assetsRes.data) ? assetsRes.data : []);
      setAssignees(Array.isArray(assigneesRes.data) ? assigneesRes.data : []);
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to load assets."));
    }
    setIsLoading(false);
  }, [adminId]);

  // Runs a change, reports the result, and reloads the list. Returns true on success.
  const run = async (request, successText, failureText) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await request();
      setSuccessMessage(successText);
      await fetchAll();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, failureText));
      return false;
    }
  };

  const createAsset = (body) =>
    run(
      () => createAssetReq({ admin_id: adminId, body }),
      ASSETS_MESSAGES.created,
      "Failed to add asset."
    );

  const updateAsset = (id, body) =>
    run(
      () => updateAssetReq({ admin_id: adminId, id, body }),
      ASSETS_MESSAGES.updated,
      "Failed to update asset."
    );

  const deleteAsset = (id) =>
    run(
      () => deleteAssetReq({ admin_id: adminId, id }),
      ASSETS_MESSAGES.deleted,
      "Failed to delete asset."
    );

  const assignAsset = (id, body) =>
    run(
      () => assignAssetReq({ admin_id: adminId, id, body }),
      ASSETS_MESSAGES.assigned,
      "Failed to assign asset."
    );

  const returnAsset = (id, body) =>
    run(
      () => returnAssetReq({ admin_id: adminId, id, body }),
      ASSETS_MESSAGES.returned,
      "Failed to return asset."
    );

  const disposeAsset = (id, body) =>
    run(
      () => disposeAssetReq({ admin_id: adminId, id, body }),
      ASSETS_MESSAGES.disposed,
      "Failed to dispose asset."
    );

  return {
    assets,
    assignees,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    createAsset,
    updateAsset,
    deleteAsset,
    assignAsset,
    returnAsset,
    disposeAsset,
    clearError: () => setErrorMessage(null),
    clearSuccess: () => setSuccessMessage(null),
  };
}

export default useAssets;
